import type { PromoCodePlatform, PromoCodeRedeemResult } from '@/utils/requests/api-requests'

const MAX_FAILED_ATTEMPTS = 5
const LOCK_DURATION_MS = 24 * 60 * 60 * 1000

export type PromoCodeLockState = { failedAttempts: number; lockedUntil: number | null }
export const PROMO_CODE_REJECTIONS = ['invalid', 'unavailable-platform', 'already-redeemed', 'limit-reached'] as const
export type PromoCodeRejection = (typeof PROMO_CODE_REJECTIONS)[number]

const REJECTION_BY_ERROR_CODE: Record<string, PromoCodeRejection> = {
    PROMO_CODE_NOT_AVAILABLE_FOR_PLATFORM: 'unavailable-platform',
    PROMO_CODE_ALREADY_REDEEMED_BY_USER: 'already-redeemed',
    PROMO_CODE_REDEMPTION_LIMIT_REACHED: 'limit-reached',
}

// a rejected code is 400/404/409; 401, 5xx, timeouts and network errors say nothing about the code,
// and a 502/504 may even hide a redemption that went through
export function isRejectedCode(result: PromoCodeRedeemResult): boolean {
    return result.status === 'error' && [400, 404, 409].includes(result.httpStatus ?? 0)
}

export function getRejection(result: PromoCodeRedeemResult): PromoCodeRejection {
    return (result.status === 'error' && result.errorCode && REJECTION_BY_ERROR_CODE[result.errorCode]) || 'invalid'
}

export function isPromoCodeLocked({ lockedUntil }: PromoCodeLockState, now: number): boolean {
    return lockedUntil !== null && now < lockedUntil
}

// ponytail: per-device counter in localStorage, gone with site data; move the limit to backend-promo-codes if codes get brute-forced
export function registerFailedAttempt({ failedAttempts }: PromoCodeLockState, now: number): PromoCodeLockState {
    const attempts = failedAttempts + 1

    return attempts >= MAX_FAILED_ATTEMPTS
        ? { failedAttempts: 0, lockedUntil: now + LOCK_DURATION_MS }
        : { failedAttempts: attempts, lockedUntil: null }
}

export function getPromoCodePlatform(env: {
    isNativeIos: boolean
    isNativeAndroid: boolean
    isNativeApp: boolean
}): PromoCodePlatform {
    if (env.isNativeIos) {
        return 'IOS_APP'
    }
    if (env.isNativeAndroid) {
        return 'ANDROID_APP'
    }
    return env.isNativeApp ? 'MOBILE_APP' : 'WEB'
}
